/**
 * The front's sound: a track for each scene's mood (music/tracks/front-moods.ts, procedural, original) through
 * C0's own music director, and ambience made in code to match what moves on the screen: rain, wind, fire
 * crackle, falling water, crickets, thunder after each lightning flash, the temple bell when it swings.
 * Browsers hold sound until a first click or key: the track is remembered until then and fades in softly.
 * Everything goes through the effects bus (the game's volume and mute apply); the front has a mute toggle.
 * The viewer picks what to hear (soundMode.ts): nature ambience, the lofi music, or both. In 'bgm' the ambience
 * (and the bell, thunder, track beds) fades out; in 'nature' the music fades out. A faint vinyl hiss and crackle
 * sits under the music whenever it plays, the lofi record feel.
 */
import type { Sfx } from '../audio';
import { music } from '../music';
import { SCENES, type SceneDef, type SceneId } from './scenes';
import type { Stage } from './stage';
import { getSoundMode, subscribeSoundMode, wantsMusic, wantsNature } from './soundMode';
import { station, TRACK_BED } from './station';

const TRACK: Record<SceneDef['mood'], string> = {
  login: 'login',
  storm: 'fstorm',
  war: 'fwar',
  moon: 'fmoon',
  calm: 'fcalm',
  bells: 'fbells',
  night: 'fnight',
  rain: 'frain',
  stele: 'fstele',
  lofi: 'lofiLake',
};

/** Channel levels (0..1) each scene wants. */
type Mix = Partial<Record<'rain' | 'wind' | 'fire' | 'water' | 'crickets' | 'vinyl', number>>;
const MIX: Record<SceneId, Mix> = {
  island: { wind: 0.25, crickets: 0.15 },
  storm: { rain: 1, wind: 0.8 },
  fort: { fire: 0.9, wind: 0.5 },
  moon: { wind: 0.3, crickets: 0.25 },
  snow: { wind: 1 },
  mist: { wind: 0.4 },
  temple: { wind: 0.25 },
  camp: { fire: 1, wind: 0.2, crickets: 0.2 },
  valley: { water: 0.8, crickets: 0.5 },
  hall: { rain: 0.6, wind: 0.2 },
  bamboo: { rain: 0.8, wind: 0.3 },
  stele: { wind: 0.2, fire: 0.25 },
  lake: { water: 0.15, wind: 0.2 },
  falls: { water: 0.9, wind: 0.15 },
  lantern: { crickets: 0.3, fire: 0.15 },
  blossom: { wind: 0.3, crickets: 0.1 },
  peak: { wind: 0.5 },
  pier: { water: 0.35, wind: 0.35 },
  village: { water: 0.15, crickets: 0.1 },
  snowfield: { wind: 0.6 },
  grove: { wind: 0.3 },
  arena: { wind: 0.25 },
  fireflies: { crickets: 0.6, fire: 0.5 },
  capital: { wind: 0.3 },
};

/** Whether the station (the lofi playlist) plays for this scene: its own scenes, and any scene in listen mode. */
let listening = false;
export function setListening(on: boolean): void {
  listening = on;
}
/** The viewer used the player: the station keeps the music for the rest of the front, whatever the scene. */
let taken = false;
export function takeStation(): void {
  taken = true;
}
const stationFor = (id: SceneId | null): boolean =>
  listening || taken || SCENES.find((s) => s.id === id)?.mood === 'lofi';

export const trackFor = (id: SceneId | null): string =>
  TRACK[SCENES.find((s) => s.id === id)?.mood ?? 'login'];

class FrontAudio {
  private sfx: Sfx | null = null;
  private toggleMute: (() => boolean) | null = null;
  private stage: Stage | null = null;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private chan: Partial<Record<keyof Mix, GainNode>> = {};
  private noise: AudioBuffer | null = null;
  private dust: GainNode | null = null;
  private lastT = 0;
  private lastScene: SceneId | null = null;
  private timer = 0;
  private on = true;

  bind(sfx: Sfx, toggle: () => boolean, stage: Stage | null): void {
    this.sfx = sfx;
    this.toggleMute = toggle;
    this.stage = stage;
    if (!this.timer) this.timer = window.setInterval(() => this.tick(), 100);
    if (!this.unsub)
      this.unsub = subscribeSoundMode(() => {
        const f = document.getElementById('front');
        if (f && !f.hidden) this.route(this.stage?.scene ?? null);
      });
  }
  private unsub: (() => void) | null = null;

  /** The scene changed: its track (the director crossfades). */
  scene(id: SceneId): void {
    const f = document.getElementById('front');
    if (f && !f.hidden) this.route(id);
  }

  /** The front's own track for the picture now (main.ts calls it when the front shows). */
  playFront(): void {
    this.route(this.stage?.scene ?? null);
  }

  /** The station's playlist, or the scene's own mood track. */
  route(id: SceneId | null): void {
    if (!wantsMusic()) {
      // Nature only: the music fades out (the director's own fade).
      station.pause();
      music.play('none');
    } else if (stationFor(id)) station.play();
    else {
      station.pause();
      music.play(trackFor(id));
    }
  }

  /** Music volume, 0–1 (the game's music setting). */
  get musicLevel(): number {
    return (this.sfx as unknown as { music?: number } | null)?.music ?? 0.5;
  }

  setMusicLevel(v: number): void {
    const sfx = this.sfx as unknown as { music: number; applyLevels(): void } | null;
    if (!sfx) return;
    sfx.music = Math.max(0, Math.min(1, v));
    sfx.applyLevels();
  }

  get muted(): boolean {
    return this.sfx?.muted ?? false;
  }

  toggle(): boolean {
    return this.toggleMute?.() ?? false;
  }

  private build(): void {
    const sfx = this.sfx as unknown as { context?: AudioContext | null; sfxOut?: GainNode | null } | null;
    const ctx = sfx?.context;
    const out = sfx?.sfxOut;
    if (!ctx || !out || this.master) return;
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(out);
    this.master = master;
    // One second of white noise, looped by every channel.
    const len = ctx.sampleRate;
    const nb = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = nb.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noise = nb;
    const src = (): AudioBufferSourceNode => {
      const s = ctx.createBufferSource();
      s.buffer = nb;
      s.loop = true;
      s.start();
      return s;
    };
    const chan = (k: keyof Mix, build: (n: AudioNode, g: GainNode) => void) => {
      const g = ctx.createGain();
      g.gain.value = 0;
      g.connect(master);
      build(src(), g);
      this.chan[k] = g;
    };
    // Rain: hissing high noise.
    chan('rain', (n, g) => {
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 2400;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 9000;
      n.connect(hp).connect(lp).connect(g);
    });
    // Wind: band noise whose centre drifts and swells.
    chan('wind', (n, g) => {
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 420;
      bp.Q.value = 1.2;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.12;
      const lg = ctx.createGain();
      lg.gain.value = 220;
      lfo.connect(lg).connect(bp.frequency);
      lfo.start();
      n.connect(bp).connect(g);
    });
    // Falling water: low rumble with a bright edge.
    chan('water', (n, g) => {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 1100;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 180;
      n.connect(hp).connect(lp).connect(g);
    });
    // Crickets: a high tone gated by a fast pulse.
    chan('crickets', (_n, g) => {
      for (const f of [4300, 4700]) {
        const o = ctx.createOscillator();
        o.frequency.value = f;
        const gate = ctx.createGain();
        gate.gain.value = 0;
        const lfo = ctx.createOscillator();
        lfo.frequency.value = f === 4300 ? 7.5 : 6.3;
        const lg = ctx.createGain();
        lg.gain.value = 0.04;
        lfo.connect(lg).connect(gate.gain);
        lfo.start();
        o.connect(gate).connect(g);
        o.start();
      }
    });
    // Vinyl: a faint tape hiss; the record's pops are scheduled in tick().
    chan('vinyl', (n, g) => {
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 5200;
      bp.Q.value = 0.5;
      n.connect(bp).connect(g);
    });
    const dust = ctx.createGain();
    dust.gain.value = 0;
    dust.connect(master);
    this.dust = dust;
    // Fire: its own crackle (scheduled pops) goes through this channel's gain.
    const fire = ctx.createGain();
    fire.gain.value = 0;
    fire.connect(master);
    this.chan.fire = fire;
  }

  private pop(level: number): void {
    const { ctx, noise, chan } = this;
    if (!ctx || !noise || !chan.fire) return;
    const s = ctx.createBufferSource();
    s.buffer = noise;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 1500 + Math.random() * 3000;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.5 * level, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.03 + Math.random() * 0.05);
    s.connect(hp).connect(g).connect(chan.fire);
    s.start(t, Math.random() * 0.8, 0.12);
  }

  /** One vinyl pop or dust tick, through the dust gain. */
  private dustPop(): void {
    const { ctx, noise, dust } = this;
    if (!ctx || !noise || !dust) return;
    const s = ctx.createBufferSource();
    s.buffer = noise;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800 + Math.random() * 4500;
    bp.Q.value = 0.7;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    const big = Math.random() < 0.08;
    g.gain.setValueAtTime(big ? 0.9 : 0.25 + Math.random() * 0.3, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + (big ? 0.03 : 0.012));
    s.connect(bp).connect(g).connect(dust);
    s.start(t, Math.random() * 0.8, 0.05);
  }

  private thunder(): void {
    const { ctx, noise, master } = this;
    if (!ctx || !noise || !master) return;
    const s = ctx.createBufferSource();
    s.buffer = noise;
    s.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 260;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(1.2, t + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.8);
    s.connect(lp).connect(g).connect(master);
    s.start(t);
    s.stop(t + 3);
  }

  private bell(level = 1): void {
    const { ctx, master } = this;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    for (const [i, m] of [1, 2.01, 2.76, 5.4].entries()) {
      const o = ctx.createOscillator();
      o.frequency.value = 196 * m;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime((0.5 * level) / (i + 1), t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 4.5 - i * 0.7);
      o.connect(g).connect(master);
      o.start(t);
      o.stop(t + 5);
    }
  }

  private async tick(): Promise<void> {
    this.build();
    const { ctx, master, stage } = this;
    if (!ctx || !master || !stage) return;
    const hostOn = document.getElementById('ink')?.classList.contains('on') ?? false;
    const id = stage.scene;
    const mode = getSoundMode();
    const nature = wantsNature(mode);
    const hearMusic = wantsMusic(mode);
    const want: Mix = this.on && hostOn && id && nature ? { ...MIX[id] } : {};
    // The station's track may bring its own bed (rain on the roof): the louder of the two.
    const bed = this.on && hostOn && nature && station.playing ? TRACK_BED[station.id] : undefined;
    if (bed)
      for (const [k, v] of Object.entries(bed) as [keyof Mix, number][]) want[k] = Math.max(want[k] ?? 0, v);
    const now = ctx.currentTime;
    master.gain.setTargetAtTime(this.on && hostOn ? 1 : 0, now, 0.6);
    if (this.on && hostOn && hearMusic) want.vinyl = 1;
    this.dust?.gain.setTargetAtTime(want.vinyl ? 0.2 : 0, now, 0.8);
    for (const [k, g] of Object.entries(this.chan) as [keyof Mix, GainNode][]) {
      const lvl =
        (want[k] ?? 0) *
        (k === 'rain' ? 0.16 : k === 'wind' ? 0.2 : k === 'water' ? 0.18 : k === 'vinyl' ? 0.012 : 0.5);
      g.gain.setTargetAtTime(lvl, now, 0.8);
    }
    if (id !== this.lastScene) {
      this.lastScene = id;
      this.lastT = stage.t;
    }
    if (!this.on || !hostOn || !id) return;
    // Vinyl pops, about two a second, a little irregular.
    if (hearMusic && Math.random() < 0.22) this.dustPop();
    if (!nature) {
      this.lastT = stage.t;
      return;
    }
    // Fire crackles at random.
    const fire = want.fire ?? 0;
    if (fire > 0 && Math.random() < 0.5 * fire) this.pop(fire);
    // Thunder trails each flash; the bell rings as it swings (the same clocks the pictures draw by).
    const t = stage.t;
    if (id === 'storm') {
      const { lightning } = await import('./scenes/storm');
      const a = lightning(this.lastT);
      const b = lightning(t);
      if (b.k !== a.k && b.u >= 0 && b.u < 1)
        window.setTimeout(() => this.thunder(), 350 + Math.random() * 600);
    }
    if (id === 'temple' && Math.floor(t / 7) !== Math.floor(this.lastT / 7)) this.bell();
    // The mountain temple's bell, far off, now and then.
    if (id === 'peak' && Math.floor(t / 31) !== Math.floor(this.lastT / 31)) this.bell(0.35);
    this.lastT = t;
  }
}

export const frontAudio = new FrontAudio();
